import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
    Container,
    MessageDrawer,
    DamageDrawer,
    SideDrawer,
    MessageForm,
} from '../../components'
import { SchedulerHeader } from './Header'
import {
    _useFinderSelection,
    usePrunedInfrastructure,
    useRootInfrastructure,
    _useWorkorder,
    useBlanket,
    useMessageDrawer,
    useUpstreamServiceContract,
    useAppDispatch,
    AreaStatusTagController,
    _LeaseController,
    AptConfigController,
    useUser,
} from '../../hooks'
import {
    CreateWorkOrderRequest,
    CreateWorkorderActionThunk,
    FinderLocationSelection,
    FinderSelectionMode,
    GetWorkorderListActionThunk,
    RootState,
    UpdateWorkOrderRequest,
    WorkorderDetailActionThunk,
    _GetWorkOrderListRequest,
    getChangeOrderConfigListRequest,
    getInventoryInspectionList,
    getPermissionRoleList,
    getStatusGroupList,
    getStatusList,
    setInventoryInspectionList,
    setUnits,
} from '../../store'

import { ScheduleFilterDrawer, WorkorderDetailDrawer } from './Drawers'
import { SchedulerBody } from './Body'

import { ChangeOrderModal, SchedulerAssignModal } from './Modals'
import {
    addWorkorderToAnalytics,
    ApartmentVendor,
    Area,
    AreaConfig,
    BaseWorkorder,
    createWorkorderAnalyticDict,
    Damage,
    getFlatInfrastructure,
    getTotalPriceExpense,
    getTotalPriceIncome,
    IdBoolMap,
    ListVendor,
    ModelListMap,
    ModelMap,
    AreaTransferTooltipMap,
    DoubleTransferChainResponse,
    Schedule,
    Service,
    SMART_SCHEDULER_PERMISSION,
    traverse,
    Unit,
    UnitVisibility,
    User,
    WorkorderStatus,
} from '../../models'
import { CircularProgress, Theme } from '@material-ui/core'
import { useScheduleFilter } from '../../hooks/useFilter'
import { BulkEditSelectionList } from '../Scheduler/Forms'
import { BudgetModal } from './Modals/BudgetModal/BudgetModal'
import { useSelector } from 'react-redux'
import { KeyTrackingModal } from './Modals/KeyTrackingModal'
import { ScheduleBlanketRoot } from './Blankets/ScheduleBlanketRoot'
import { ChangeOrderListModal } from '../ChangeOrder/ChangeOrderListModal'
import { hasPermission } from '../../models/Users/services'
import { axiosInstance } from '../../helpers'
import { ManualUnitReadyMark, ManualUnitReadyStatus } from './unitReady'
import { toast } from 'react-toastify'
import { useChangeOrder } from '../../hooks/useChangeOrder'
import { useLoriState } from '../../contexts/LoriContext'
import { useTimezone } from '../../contexts/timezone/timezone'
import {
    SimpleUnitInspection,
    useUnitInspections,
} from '../../hooks/useUnitInspections'
import { UnitInspectionDetailForm } from '../Inspection/UnitInspectionDetailForm'
import { ScheduleModal } from './Modals/ScheduleModal/ScheduleModal'
import { ScheduleMethod, SchedulePhase } from './Modals/ScheduleModal/types'
import {
    AddLineItemParams,
    AddTemplateServiceParams,
    AddTemplateVendorParams,
    UpdateTemplateServiceParams,
    DeleteTemplateServiceParams,
    DeleteTemplateVendorParams,
    UpdateTemplateVendorParams,
    DeleteLineItemParams,
} from '../../hooks/useEzSchedule/types'
import { ezScheduleDetails } from '../../models'

const DRAWER_ANIMATION_DELAY_MS = 250

interface Props {
    theme: Theme
    scheduleDetail: Schedule
    aptConfigController: AptConfigController
    astController: AreaStatusTagController
    leaseController: _LeaseController
    serviceList: Service[]
    vendorList: ListVendor[]
    areaConfigMap: ModelMap<AreaConfig>
    rootUser: User
    unitWorkorderMap: ModelListMap<BaseWorkorder>
    createWorkorder: (req: CreateWorkOrderRequest) => CreateWorkorderActionThunk
    updateWorkorder: (req: UpdateWorkOrderRequest) => WorkorderDetailActionThunk
    incrementWorkorderMessageCount: (workorder: BaseWorkorder) => void
    workorderList: BaseWorkorder[]
    getWorkorderList: (
        req: _GetWorkOrderListRequest,
    ) => GetWorkorderListActionThunk
    loadingList: boolean
    isSpreadsheetView: boolean
    setIsSpreadsheetView: (value: boolean) => void
    ezSchedule: ezScheduleDetails
    ezScheduleLoading: boolean
    addTemplateService: (params: AddTemplateServiceParams) => void
    addLineItem: (params: AddLineItemParams) => void
    deleteLineItem: (params: DeleteLineItemParams) => void
    addTemplateVendor: (params: AddTemplateVendorParams) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    updateTemplateVendor: (params: UpdateTemplateVendorParams) => void
    deleteTemplateVendor: (params: DeleteTemplateVendorParams) => void
    apartmentVendorList: ApartmentVendor[]
    templateInputRevision: number
}

export const Scheduler = (props: Props) => {
    const {
        theme,
        astController,
        areaConfigMap,
        scheduleDetail,
        leaseController,
        serviceList,
        vendorList,
        aptConfigController,
        unitWorkorderMap,
        createWorkorder,
        updateWorkorder,
        incrementWorkorderMessageCount,
        workorderList,
        getWorkorderList,
        loadingList: workordersLoading,
        isSpreadsheetView,
        setIsSpreadsheetView,
        ezSchedule,
        ezScheduleLoading,
        addTemplateService,
        addLineItem,
        deleteLineItem,
        addTemplateVendor,
        updateTemplateService,
        deleteTemplateService,
        updateTemplateVendor,
        deleteTemplateVendor,
        apartmentVendorList,
        templateInputRevision,
    } = props

    const permissionRoleList = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )

    const {
        areaStatusTagList,
        editAreaStatusTag,
        areaStatusTagMap,
        getAreaStatusTags,
        bulkEditAreaStatusTag,
        bulkSwapAreaStatusTag,
    } = astController

    const { damageList, createDamage } = leaseController
    const {
        unitConfigList,
        getUnitConfigMap,
        getInventoryConfigMap,
    } = aptConfigController
    const unitConfigMap = getUnitConfigMap()
    const { setHighlightEZSchedulerIcon } = useLoriState()
    const {
        unitInspectionUnitMap,
        unitInspectionDetail,
        loadingUnitInspectionDetail,
        getUnitInspections,
        setUnitInspectionDetail,
        getUnitInspectionDetail,
    } = useUnitInspections()

    const inventoryConfigMap = getInventoryConfigMap()

    // Forms
    const history = useHistory()
    const location = useLocation<{ openAutoScheduleSetup?: boolean }>()

    const [
        scheduleMethodSelectionModalOpen,
        setScheduleMethodSelectionModalOpen,
    ] = useState(false)
    const [
        scheduleModalInitialMethod,
        setScheduleModalInitialMethod,
    ] = useState<ScheduleMethod | null>(null)
    const [
        scheduleModalInitialPhase,
        setScheduleModalInitialPhase,
    ] = useState<SchedulePhase>('setup')
    const [dtChainOpen, setDtChainOpen] = useState(false)
    const [dtChainAlertMode, setDtChainAlertMode] = useState(false)
    const [pendingDtChainAlert, setPendingDtChainAlert] = useState(false)

    useEffect(() => {
        if (!location.state?.openAutoScheduleSetup) return
        setScheduleModalInitialMethod('auto_scheduler')
        setScheduleModalInitialPhase('setup')
        setScheduleMethodSelectionModalOpen(true)
        history.replace({
            pathname: location.pathname,
            search: location.search,
            state: {},
        })
    }, [location.state?.openAutoScheduleSetup])

    useEffect(() => {
        if (scheduleMethodSelectionModalOpen || !pendingDtChainAlert) {
            return
        }
        setDtChainOpen(true)
        setDtChainAlertMode(true)
        setPendingDtChainAlert(false)
    }, [scheduleMethodSelectionModalOpen, pendingDtChainAlert])
    const [assignModalOpen, setAssignModalOpen] = useState(false)
    const [workorderDetail, setWorkorderDetail] = useState<
        { workorder: BaseWorkorder; unit: Unit } | undefined
    >(undefined)

    const [changeOrderModalOpen, setChangeOrderModalOpen] = useState<
        BaseWorkorder | undefined
    >(undefined)

    const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false)

    const [openKeyTracking, setOpenKeyTracking] = useState(false)

    const {
        scheduleFilterOpen,
        setScheduleFilterOpen,
        shouldRefresh,
        setShouldRefresh,
    } = useLoriState()
    const [damageUnit, setDamageUnit] = useState<Unit | undefined>()
    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [serviceBudgetModalOpen, setServiceBudgetModalOpen] = useState(false)

    const [editWorkorderSelection, setEditWorkorderSelection] = useState<
        ModelMap<BaseWorkorder>
    >({})

    const [vacantAreas, setVacantAreas] = useState<Area[]>([])

    const [openProjectNotes, setOpenProjectNotes] = useState(false)

    const [openChangeOrderListModal, setOpenChangeOrderListModal] = useState(
        false,
    )

    const [
        bulkLockAreaStatusTagLoading,
        setBulkLockAreaStatusTagLoading,
    ] = useState(false)

    const [openUnitInspectionDrawer, setOpenUnitInspectionDrawer] = useState(
        false,
    )

    const contentRef = useRef<HTMLDivElement>(null)

    const dispatch = useAppDispatch()

    const { workspaceUser } = useUser()
    const timezoneState = useTimezone()

    const changeOrderController = useChangeOrder()

    const { setChannel } = useMessageDrawer()

    const scheduleBudgetItemList = useSelector(
        (state: RootState) => state.workorder.schedule.scheduleBudgetItems,
    )

    const changeOrderConfigList = useSelector(
        (state: RootState) => state.aptConfig.changeOrderConfigList,
    )

    const locationFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'schedule',
    })

    const assignmentFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.PrunedSelection,
        selectionMode: FinderSelectionMode.Recursive,
        useCase: 'schedule',
    })

    const { tree, unitList } = useRootInfrastructure(true)

    const [serviceAreaMap, setServiceAreaMap] = useState<IdBoolMap>({})
    const [manualUnitReady, setManualUnitReady] = useState<
        Map<number, ManualUnitReadyMark>
    >(new Map())
    const [areaSelection, setAreaSelection] = useState<IdBoolMap>({})
    const [
        areaTransferTooltipMap,
        setAreaTransferTooltipMap,
    ] = useState<AreaTransferTooltipMap>({})

    useEffect(() => {
        if (!scheduleDetail?.id) {
            return
        }
        let cancelled = false
        axiosInstance
            .get<AreaTransferTooltipMap>(
                `workorder/schedule/${scheduleDetail.id}/area-transfer-tooltips/`,
            )
            .then((response) => {
                if (!cancelled) {
                    setAreaTransferTooltipMap(response.data ?? {})
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAreaTransferTooltipMap({})
                }
            })
        return () => {
            cancelled = true
        }
    }, [scheduleDetail.id])

    const root = tree.root

    // Build required Model maps
    const filterController = useScheduleFilter(
        unitWorkorderMap,
        {},
        areaStatusTagMap,
        serviceAreaMap,
        areaConfigMap,
        unitConfigMap,
        vacantAreas,
        unitList,
        undefined,
        manualUnitReady,
    )

    const prunedTree = usePrunedInfrastructure(
        tree,
        locationFinderSelection.selection,
        filterController.filter,
        filterController.isUnitInFilter,
    )
    const visibleInfrastructure = useMemo(
        () => getFlatInfrastructure(prunedTree.root),
        [prunedTree],
    )

    // schedule header nav stat calculations
    // these follow the filters
    const visibleUnits = useMemo(
        () =>
            visibleInfrastructure.filter(
                (item): item is Unit =>
                    (item as Unit).visibility ===
                        UnitVisibility.ALWAYS_VISIBLE ||
                    (item as Unit).visibility === UnitVisibility.SCHEDULE_ONLY,
            ),
        [visibleInfrastructure],
    )
    const visibleInfrastructureUnitCount = visibleUnits.length
    const visibleInfrastructureAreaCount = useMemo(
        () =>
            visibleUnits.reduce((areaCount, unit) => {
                return areaCount + unit.areas.length
            }, 0),
        [visibleUnits],
    )
    const visibleAreaConfigBreakdown = useMemo(() => {
        const breakdown: { [name: string]: number } = {}
        visibleUnits.forEach((unit) => {
            unit.areas.forEach((area) => {
                const name = area.area_config_name ?? 'Unknown'
                breakdown[name] = (breakdown[name] ?? 0) + 1
            })
        })
        return breakdown
    }, [visibleUnits])
    const visibleUnitIdSet = useMemo(
        () => new Set(visibleUnits.map((unit) => unit.id)),
        [visibleUnits],
    )
    const ghostServiceAreasCount = useMemo(() => {
        return Object.entries(unitWorkorderMap).reduce(
            (ghostAreaCount, [unitId, value]) => {
                if (!Array.isArray(value)) {
                    return ghostAreaCount
                }

                const numericUnitId = Number(unitId)
                if (
                    Number.isNaN(numericUnitId) ||
                    !visibleUnitIdSet.has(numericUnitId)
                ) {
                    return ghostAreaCount
                }

                const unitGhostAreaCount = value.reduce(
                    (unitCount, workorder) => {
                        const isGhostWorkorder =
                            workorder.vendor_id === null ||
                            workorder.service_id === null ||
                            workorder.start_date === null

                        if (!isGhostWorkorder) {
                            return unitCount
                        }

                        return unitCount + workorder.service_area_list.length
                    },
                    0,
                )

                return ghostAreaCount + unitGhostAreaCount
            },
            0,
        )
    }, [unitWorkorderMap, visibleUnitIdSet])

    // Unit search loading overlay - only shown when applying filter (not during typing)
    const [isUnitSearching, setIsUnitSearching] = useState(false)
    const onUnitSearchingChange = useCallback((searching: boolean) => {
        setIsUnitSearching(searching)
    }, [])

    const analyticRect = useBlanket(analyticsModalOpen, contentRef)

    const [workordersInFilter, setWorkordersInFilter] = useState<
        BaseWorkorder[]
    >([])
    const [analyticDict, setAnalyticDict] = useState(
        createWorkorderAnalyticDict(),
    )

    const [unitDamageMap, setUnitDamageMap] = useState<ModelListMap<Damage>>({})

    const networkRequestsComplete =
        scheduleDetail !== undefined &&
        damageList !== undefined &&
        unitWorkorderMap.readyForUse === true &&
        areaStatusTagList !== undefined
    const scheduleReviewEnabled =
        !ezScheduleLoading && templateInputRevision && templateInputRevision > 0
            ? true
            : false
    const upstreamContractMap = useUpstreamServiceContract()

    useEffect(() => {
        if (!scheduleDetail?.id) return
        let cancelled = false
        axiosInstance
            .get<
                {
                    id: number
                    unit_id: number
                    status: ManualUnitReadyStatus
                    marked_by_name: string | null
                    marked_date: string | null
                }[]
            >(`workorder/schedule-unit-ready/`, {
                params: { schedule: scheduleDetail.id },
            })
            .then((res) => {
                if (!cancelled) {
                    setManualUnitReady(
                        new Map(
                            res.data.map((row) => [
                                row.unit_id,
                                {
                                    id: row.id,
                                    status: row.status,
                                    markedByName: row.marked_by_name,
                                    markedDate: row.marked_date,
                                },
                            ]),
                        ),
                    )
                }
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [scheduleDetail.id])

    const setUnitReadyStatus = useCallback(
        async (unitId: number, status: ManualUnitReadyStatus | null) => {
            if (status == null) {
                const existing = manualUnitReady.get(unitId)
                if (existing) {
                    await axiosInstance
                        .delete(`workorder/schedule-unit-ready/${existing.id}/`)
                        .catch(() => {})
                }
                setManualUnitReady((prev) => {
                    const next = new Map(prev)
                    next.delete(unitId)
                    return next
                })
                return
            }

            const res = await axiosInstance
                .post<{
                    id: number
                    status: ManualUnitReadyStatus
                    marked_by_name: string | null
                    marked_date: string | null
                }>(`workorder/schedule-unit-ready/`, {
                    schedule_id: scheduleDetail.id,
                    unit_id: unitId,
                    status,
                })
                .catch(() => null)
            if (res == null) {
                return
            }
            setManualUnitReady((prev) => {
                const next = new Map(prev)
                next.set(unitId, {
                    id: res.data.id,
                    status: res.data.status,
                    markedByName: res.data.marked_by_name,
                    markedDate: res.data.marked_date,
                })
                return next
            })
        },
        [scheduleDetail.id, manualUnitReady],
    )

    const [openUnitNotes, setOpenUnitNotes] = useState(false)
    const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>()

    useEffect(() => {
        if (!changeOrderConfigList) {
            dispatch(getChangeOrderConfigListRequest({}))
        }

        const newDate = new Date()
        newDate.setMonth(newDate.getMonth() - 6)
        changeOrderController.getChangeOrderList({
            lower_bound_date: newDate.toISOString(),
            schedule_id: scheduleDetail.id,
        })

        if (scheduleDetail.inspection) {
            getUnitInspections(scheduleDetail.inspection)
        }

        dispatch(getStatusGroupList({}))
        dispatch(getStatusList({}))
        dispatch(getPermissionRoleList({}))
    }, [])

    useEffect(() => {
        // when a workorder is updated, if there is a selected workorder, update it
        if (workorderDetail && unitWorkorderMap.readyForUse) {
            const updatedWo = unitWorkorderMap[workorderDetail.unit.id]?.find(
                (wo) => wo.id === workorderDetail.workorder.id,
            )
            if (updatedWo) {
                setWorkorderDetail({
                    unit: workorderDetail.unit,
                    workorder: updatedWo,
                })
            }
        }
    }, [unitWorkorderMap])

    useEffect(() => {
        if (networkRequestsComplete) {
            let curDamageIdx = 0
            const mutableAnalyticDict = createWorkorderAnalyticDict()
            const mutableWorkordersInFilter: BaseWorkorder[] = []
            const mutableDamageMap: ModelListMap<Damage> = {}

            traverse(prunedTree.root, (folder) => {
                folder.units.forEach((unit) => {
                    if (!filterController.isUnitInFilter(unit)) {
                        return
                    }

                    if (damageList && damageList.length > 0) {
                        while (
                            damageList[curDamageIdx] &&
                            damageList[curDamageIdx].unit === unit.id
                        ) {
                            if (mutableDamageMap[unit.id] === undefined) {
                                mutableDamageMap[unit.id] = []
                            }
                            mutableDamageMap[unit.id]!.push(
                                damageList[curDamageIdx],
                            )
                            curDamageIdx += 1
                        }
                    }

                    mutableAnalyticDict.unitCount++
                    if (unit.id in unitWorkorderMap) {
                        const unitWorkorders = unitWorkorderMap[unit.id]
                        unitWorkorders?.forEach((wo) => {
                            if (
                                filterController.isWorkorderInFilter(wo, unit)
                            ) {
                                addWorkorderToAnalytics(mutableAnalyticDict, wo)
                                mutableWorkordersInFilter.push(wo)
                                const woPrice = getTotalPriceExpense(wo)

                                if (wo.service_id) {
                                    if (
                                        !(
                                            wo.service_id in
                                            mutableAnalyticDict.serviceBudgets
                                        )
                                    ) {
                                        mutableAnalyticDict.serviceBudgets[
                                            wo.service_id
                                        ] = {
                                            serviceName: wo.service_name ?? '',
                                            totalBudget: 0,
                                            invoicedBudget: 0,
                                        }
                                    }

                                    mutableAnalyticDict.incomeForecast += getTotalPriceIncome(
                                        wo,
                                        upstreamContractMap,
                                    )
                                    mutableAnalyticDict.forecastedBudget += woPrice
                                    mutableAnalyticDict.serviceForecastedBudget += woPrice
                                    mutableAnalyticDict.serviceBudgets[
                                        wo.service_id
                                    ].totalBudget += woPrice
                                    if (
                                        wo.status === WorkorderStatus.INVOICED
                                    ) {
                                        mutableAnalyticDict.serviceBudgets[
                                            wo.service_id
                                        ].invoicedBudget += woPrice
                                        mutableAnalyticDict.invoicedBudget += woPrice
                                    } else if (
                                        wo.status ===
                                        WorkorderStatus.INVOICED_OUT
                                    ) {
                                        mutableAnalyticDict.invoicedOutBudget += woPrice
                                    }
                                }
                            }
                        })
                    }
                })
            })

            // for each schedule budget item, add its cost to forecasted cost
            scheduleBudgetItemList?.forEach((item) => {
                mutableAnalyticDict.forecastedBudget += item.cost
                mutableAnalyticDict.lineItemForecastedBudget += item.cost
            })

            const hiddenOccUnitCount =
                unitList?.filter((unit) => {
                    return (
                        (unit.visibility === UnitVisibility.NOT_VISIBLE ||
                            unit.visibility ===
                                UnitVisibility.INSPECTION_ONLY) &&
                        unit.areas.some((area) => {
                            return areaConfigMap[area.area_config]?.occupiable
                        })
                    )
                }).length ?? 0

            mutableAnalyticDict.hiddenOccUnitCount = hiddenOccUnitCount

            setWorkordersInFilter(mutableWorkordersInFilter)
            setAnalyticDict(mutableAnalyticDict)
            setUnitDamageMap(mutableDamageMap)
        }
    }, [
        scheduleDetail,
        scheduleBudgetItemList,
        damageList,
        unitWorkorderMap,
        filterController.filter,
        prunedTree,
    ])

    const downloadUnitNotesReport = () => {
        const units: number[] = []
        Object.keys(locationFinderSelection.selection.unit).forEach((unit) => {
            if (unit !== 'length') {
                units.push(parseInt(unit))
            }
        })

        return axiosInstance
            .post('infrastructure/unit/get_unit_notes_report/', {
                units: units,
            })
            .then(() => {
                toast.success(
                    'You will Recieve an email when the report is done',
                )
            })
            .catch((err) => {
                toast.error(err.reponse.data.message)
            })
    }

    const onClickUnitInspection = (
        simpleUnitInspection: SimpleUnitInspection,
    ) => {
        setOpenUnitInspectionDrawer(true)
        getUnitInspectionDetail(simpleUnitInspection.id)
        dispatch(
            getInventoryInspectionList({
                params: {
                    inspection: Number(scheduleDetail.inspection),
                    units: `${simpleUnitInspection.unit_id}`,
                },
            }),
        )
    }

    const onCloseUnitInspectionDrawer = () => {
        setOpenUnitInspectionDrawer(false)
        setUnitInspectionDetail(null)
        dispatch(setInventoryInspectionList(undefined))
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                width: '100%',
                height: 'calc(100vh - 104px)',
            }}
        >
            {/* Header */}
            <SchedulerHeader
                refreshWorkorders={() => {
                    getWorkorderList({
                        params: { schedule: scheduleDetail.id },
                    })
                }}
                clickSchedule={() => {
                    if (
                        hasPermission(workspaceUser, SMART_SCHEDULER_PERMISSION)
                    ) {
                        setScheduleModalInitialMethod(null)
                        setScheduleModalInitialPhase('setup')
                        setScheduleMethodSelectionModalOpen(true)
                        setHighlightEZSchedulerIcon(false)
                    } else {
                        window.alert(
                            "You don't have permission to use the smart scheduler.",
                        )
                    }
                }}
                clickScheduleReview={() => {
                    if (
                        hasPermission(workspaceUser, SMART_SCHEDULER_PERMISSION)
                    ) {
                        setScheduleModalInitialMethod('auto_scheduler')
                        setScheduleModalInitialPhase('review')
                        setScheduleMethodSelectionModalOpen(true)
                        setHighlightEZSchedulerIcon(false)
                    } else {
                        window.alert(
                            "You don't have permission to use the smart scheduler.",
                        )
                    }
                }}
                schedule={scheduleDetail}
                filterOpen={scheduleFilterOpen}
                clickAssign={() => setAssignModalOpen(true)}
                clickFilter={() => {
                    setScheduleFilterOpen(!scheduleFilterOpen)
                    setOpenUnitNotes(false)
                }}
                clickBudget={() =>
                    setServiceBudgetModalOpen(!serviceBudgetModalOpen)
                }
                clickAnalytics={() =>
                    setAnalyticsModalOpen(!analyticsModalOpen)
                }
                clickBulkEdit={() => setEditMode(!editMode)}
                filterController={filterController}
                onUnitSearchingChange={onUnitSearchingChange}
                locationSelection={locationFinderSelection.selection}
                setLocationSelection={
                    locationFinderSelection.setFinderSelection
                }
                serviceList={serviceList}
                vendorList={vendorList}
                unitConfigList={unitConfigList}
                analyticDict={analyticDict}
                analyticsOpen={analyticsModalOpen}
                editMode={editMode}
                visibleInfrastructureUnitCount={visibleInfrastructureUnitCount}
                visibleInfrastructureAreaCount={visibleInfrastructureAreaCount}
                visibleAreaConfigBreakdown={visibleAreaConfigBreakdown}
                ghostServiceAreasCount={ghostServiceAreasCount}
                visibleUnitIdSet={visibleUnitIdSet}
                manualUnitReady={manualUnitReady}
                setEditMode={(editMode) => {
                    setScheduleFilterOpen(false)
                    setWorkorderDetail(undefined)
                    setAssignModalOpen(false)
                    setEditMode(editMode)
                    setOpenUnitNotes(false)
                }}
                workorderList={workordersInFilter}
                clickKeyTracking={() => setOpenKeyTracking(true)}
                areaStatusTagList={areaStatusTagList}
                openProjectNotes={openProjectNotes}
                clickProjectNotes={() => {
                    setOpenProjectNotes(!openProjectNotes)
                    setScheduleFilterOpen(false)
                    setWorkorderDetail(undefined)
                    setAssignModalOpen(false)
                    setOpenUnitNotes(false)
                }}
                clickChangeOrder={() => {
                    setOpenChangeOrderListModal(!openChangeOrderListModal)
                }}
                downloadUnitNotesReport={downloadUnitNotesReport}
                fullWorkorderList={workorderList}
                bulkLockAreaStatusTags={(locked) => {
                    setBulkLockAreaStatusTagLoading(true)
                    bulkEditAreaStatusTag({
                        body: {
                            schedule: scheduleDetail.id,
                            locked: locked,
                        },
                    }).then(() => {
                        setBulkLockAreaStatusTagLoading(false)
                    })
                }}
                isSpreadsheetView={isSpreadsheetView}
                setIsSpreadsheetView={setIsSpreadsheetView}
                scheduleReviewEnabled={scheduleReviewEnabled}
                dtChainOpen={dtChainOpen}
                dtChainAlertMode={dtChainAlertMode}
                onDtChainOpen={() => {
                    setDtChainAlertMode(false)
                    setDtChainOpen(true)
                }}
                onDtChainClose={() => {
                    setDtChainOpen(false)
                    setDtChainAlertMode(false)
                }}
            />

            {/* Body */}
            {workordersLoading || bulkLockAreaStatusTagLoading ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={100} />
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        position: 'relative',
                        minHeight: 0,
                    }}
                >
                    <SchedulerBody
                        theme={theme}
                        workspaceUser={workspaceUser}
                        prunedTree={prunedTree.root}
                        unitConfigMap={unitConfigMap}
                        areaConfigMap={areaConfigMap}
                        unitWorkorderMap={unitWorkorderMap}
                        schedule={scheduleDetail}
                        unitDamageMap={unitDamageMap}
                        serviceAreaMap={serviceAreaMap}
                        selectedWorkorder={workorderDetail?.workorder}
                        editMode={editMode}
                        editWorkorderSelection={editWorkorderSelection}
                        areaStatusTagMap={areaStatusTagMap}
                        areaTransferTooltipMap={areaTransferTooltipMap}
                        areaStatusConfigList={
                            aptConfigController.areaStatusConfigList ?? []
                        }
                        selectedUnit={damageUnit}
                        upstreamContractMap={upstreamContractMap}
                        vacantAreas={vacantAreas}
                        filterController={filterController}
                        manualUnitReady={manualUnitReady}
                        setUnitReadyStatus={setUnitReadyStatus}
                        onClickChangeOrder={(workorder) => {
                            if (workorder.id === changeOrderModalOpen?.id) {
                                setChangeOrderModalOpen(undefined)
                            } else {
                                setChangeOrderModalOpen(workorder)
                            }
                        }}
                        clickDamage={(unit) => {
                            setWorkorderDetail(undefined)
                            setDamageUnit(unit)
                            setOpenUnitNotes(false)
                        }}
                        onSelectWorkorder={(workorder, unit) => {
                            setDamageUnit(undefined)
                            if (editMode) {
                                const newEditSelection = {
                                    ...editWorkorderSelection,
                                }
                                const tempSelectionMap = { ...areaSelection }

                                if (
                                    newEditSelection[workorder.id] !== undefined
                                ) {
                                    delete newEditSelection[workorder.id]
                                    workorder.service_area_list.forEach(
                                        (area) => {
                                            delete tempSelectionMap[
                                                area.area_id
                                            ]
                                        },
                                    )
                                } else {
                                    newEditSelection[workorder.id] = workorder
                                    workorder.service_area_list.forEach(
                                        (area) => {
                                            tempSelectionMap[
                                                area.area_id
                                            ] = true
                                        },
                                    )
                                }

                                setAreaSelection(tempSelectionMap)
                                setEditWorkorderSelection(newEditSelection)
                            } else {
                                // If the user selects the already open workorder, close the drawer
                                if (
                                    workorderDetail?.workorder.id ===
                                    workorder.id
                                ) {
                                    setWorkorderDetail(undefined)
                                    return
                                }

                                setWorkorderDetail({
                                    workorder: workorder,
                                    unit: unit,
                                })
                                setOpenUnitNotes(false)
                            }
                        }}
                        clickAssign={(unit) => {
                            // Clear the old selection
                            assignmentFinderSelection.setFinderSelection()
                            // Add the new selection
                            assignmentFinderSelection.setFinderSelection({
                                location: unit,
                                type: 'unit',
                            })
                            setAssignModalOpen(true)
                            setOpenUnitNotes(false)
                        }}
                        contentRef={contentRef}
                        clickUnitNotes={(selectedUnit) => {
                            setMessageDrawerOpen(false)
                            setOpenUnitNotes(false)
                            if (selectedUnit.channel_id) {
                                setChannel(selectedUnit.channel_id)
                            }
                            setTimeout(() => {
                                setSelectedUnit(selectedUnit)
                                setOpenUnitNotes(true)
                            }, DRAWER_ANIMATION_DELAY_MS)
                            if (selectedUnit.unread_unit_notes !== 0) {
                                const newUnitList = unitList?.map((unit) => {
                                    if (selectedUnit.id === unit.id) {
                                        return {
                                            ...unit,
                                            unread_unit_notes: 0,
                                        }
                                    }
                                    return unit
                                })
                                dispatch(setUnits(newUnitList ?? []))
                            }
                        }}
                        bulkEditAreaStatusTag={bulkEditAreaStatusTag}
                        timezoneState={timezoneState}
                        unitInspectionUnitMap={unitInspectionUnitMap}
                        onClickUnitInspection={onClickUnitInspection}
                    />
                    {isUnitSearching && (
                        <Container
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 10,
                            }}
                        >
                            <CircularProgress size={48} />
                        </Container>
                    )}
                </Container>
            )}

            {/* Drawers */}

            {scheduleDetail.channel && (
                <SideDrawer
                    open={openProjectNotes}
                    width={420}
                    handleClose={() => {
                        setOpenProjectNotes(false)
                    }}
                    title={'Project Notes'}
                >
                    <MessageForm
                        theme={theme}
                        channelId={scheduleDetail.channel}
                    />
                </SideDrawer>
            )}

            <ChangeOrderListModal
                open={openChangeOrderListModal}
                theme={theme}
                changeOrderConfigList={changeOrderConfigList ?? []}
                workspaceUser={workspaceUser}
                changeOrderController={changeOrderController}
                onClose={() => setOpenChangeOrderListModal(false)}
            />

            <ScheduleFilterDrawer
                open={scheduleFilterOpen}
                filter={filterController.filter}
                setInfrastructureFilter={
                    filterController.setInfrastructureFilter
                }
                onClose={() => setScheduleFilterOpen(false)}
                vendorList={vendorList}
                serviceList={serviceList}
                unitConfigList={unitConfigList}
                areaStatusConfigList={
                    aptConfigController.areaStatusConfigList ?? []
                }
                workorderList={workorderList ?? []}
                areaConfigMap={areaConfigMap}
                areaStatusTagMap={areaStatusTagMap}
                unitWorkorderMap={unitWorkorderMap}
                locationFinderSelection={locationFinderSelection}
                root={root}
                vacantAreas={vacantAreas}
                setVacantAreas={setVacantAreas}
                bulkSwapAreaStatusTag={bulkSwapAreaStatusTag}
                scheduleId={scheduleDetail.id}
            />

            <WorkorderDetailDrawer
                workorder={workorderDetail?.workorder}
                onClose={() => setWorkorderDetail(undefined)}
                openMessages={() => setMessageDrawerOpen(true)}
                updateWorkorder={updateWorkorder}
                vendorList={vendorList}
                unit={workorderDetail?.unit}
                unitConfig={
                    unitConfigMap[workorderDetail?.unit.unit_config ?? -1]
                }
                areaConfigMap={areaConfigMap}
                schedule={scheduleDetail}
                serviceList={serviceList}
                permissionRoles={permissionRoleList ?? []}
            />

            <DamageDrawer
                unit={damageUnit}
                openMessages={(damage: Damage) => {
                    setChannel(damage.channel)
                    setMessageDrawerOpen(true)
                }}
                handleClose={() => setDamageUnit(undefined)}
                areaConfigMap={areaConfigMap}
                createDamage={createDamage}
                inspection={scheduleDetail?.inspection}
                unitDamages={unitDamageMap[damageUnit?.id ?? -1]}
            />

            <MessageDrawer
                open={messageDrawerOpen}
                onClose={() => setMessageDrawerOpen(false)}
                onCreateMessage={() => {
                    if (workorderDetail?.workorder) {
                        incrementWorkorderMessageCount(
                            workorderDetail?.workorder,
                        )
                    }
                }}
                secondaryChannelId={workorderDetail?.workorder?.inv_ins_channel}
                secondaryTitle="From Inventory Inspection"
            />

            {selectedUnit && (
                <MessageDrawer
                    open={openUnitNotes}
                    onClose={() => {
                        if (workorderDetail?.workorder) {
                            setChannel(workorderDetail.workorder.channel)
                        }
                        setOpenUnitNotes(false)
                        setSelectedUnit(undefined)
                    }}
                    onCreateMessage={() => {
                        const newUnitList = unitList?.map((unit) => {
                            if (unit.id === selectedUnit.id) {
                                return {
                                    ...unit,
                                    unit_notes_msg_count:
                                        unit.unit_notes_msg_count + 1,
                                }
                            }
                            return unit
                        })
                        dispatch(setUnits(newUnitList ?? []))
                    }}
                    title={`Unit Notes ${selectedUnit.name}`}
                />
            )}

            <BulkEditSelectionList
                open={editMode}
                selectedWorkorders={editWorkorderSelection}
                workordersInFilter={workordersInFilter}
                vendorList={vendorList}
                handleClose={() => setEditMode(false)}
                removeWorkorder={(id) => {
                    const newEditSelection = {
                        ...editWorkorderSelection,
                    }
                    delete newEditSelection[id]
                    setEditWorkorderSelection(newEditSelection)
                }}
                setSelectedWorkorders={setEditWorkorderSelection}
                upStreamContractMap={upstreamContractMap}
                areaConfigMap={areaConfigMap}
                unitList={unitList ?? []}
                areaSelection={areaSelection}
                setAreaSelection={setAreaSelection}
                serviceList={serviceList}
                permissionRoles={permissionRoleList ?? []}
            />

            <SchedulerAssignModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                filteredTree={prunedTree}
                serviceList={serviceList}
                unitConfigMap={unitConfigMap}
                unitWorkorderMap={unitWorkorderMap}
                // TODO: EZTK-280
                // areaLeaseMap={areaLeaseMap}
                areaLeaseMap={{}}
                schedule={scheduleDetail}
                areaConfigMap={areaConfigMap}
                vendorList={vendorList}
                serviceAreaMap={serviceAreaMap}
                createWorkorder={createWorkorder}
                assignmentFinderSelection={assignmentFinderSelection}
                isAreaInFilter={filterController.isAreaInFilter}
                areaStatusTagMap={areaStatusTagMap}
                areaStatusConfigList={
                    aptConfigController.areaStatusConfigList ?? []
                }
            />

            <BudgetModal
                open={serviceBudgetModalOpen}
                onClose={() => setServiceBudgetModalOpen(false)}
                workorders={workorderList}
                serviceList={serviceList}
                scheduleDetail={scheduleDetail}
                ezSchedule={ezSchedule}
                ezScheduleLoading={ezScheduleLoading}
                addTemplateService={addTemplateService}
                addLineItem={addLineItem}
                deleteLineItem={deleteLineItem}
                addTemplateVendor={addTemplateVendor}
                updateTemplateService={updateTemplateService}
                deleteTemplateService={deleteTemplateService}
                updateTemplateVendor={updateTemplateVendor}
                deleteTemplateVendor={deleteTemplateVendor}
            />

            <ChangeOrderModal
                workorder={changeOrderModalOpen}
                serviceList={serviceList}
                changeOrderConfigList={
                    aptConfigController.changeOrderConfigList
                }
                onClose={() => setChangeOrderModalOpen(undefined)}
                areaConfigMap={areaConfigMap}
                vendorList={vendorList}
                schedule={scheduleDetail}
            />

            <ScheduleBlanketRoot
                open={analyticsModalOpen}
                theme={theme}
                rect={analyticRect}
                infrastructure={prunedTree.root}
                onClose={() => setAnalyticsModalOpen(false)}
                unitWorkorderMap={unitWorkorderMap}
                isWorkorderInFilter={filterController.isWorkorderInFilter}
                workorders={workorderList}
            />

            <KeyTrackingModal
                schedule={scheduleDetail}
                open={openKeyTracking}
                areaConfigMap={areaConfigMap}
                areaStatusTagList={areaStatusTagList}
                updateAreaStatusTag={editAreaStatusTag}
                handleClose={() => setOpenKeyTracking(false)}
                visibleUnits={visibleUnits}
            />

            {workspaceUser?.active_workspace && (
                <ScheduleModal
                    open={scheduleMethodSelectionModalOpen}
                    onClose={() => setScheduleMethodSelectionModalOpen(false)}
                    initialMethod={scheduleModalInitialMethod}
                    initialPhase={scheduleModalInitialPhase}
                    infrastructure={tree.root}
                    scheduleId={scheduleDetail.id}
                    serviceList={serviceList}
                    vendorList={vendorList}
                    schedule={scheduleDetail}
                    apartment={workspaceUser.active_workspace}
                    filteredTree={prunedTree}
                    unitConfigMap={unitConfigMap}
                    areaConfigMap={areaConfigMap}
                    unitWorkorderMap={unitWorkorderMap}
                    areaLeaseMap={{}}
                    serviceAreaMap={serviceAreaMap}
                    assignmentFinderSelection={assignmentFinderSelection}
                    areaStatusTagMap={areaStatusTagMap}
                    areaStatusConfigList={
                        aptConfigController.areaStatusConfigList ?? []
                    }
                    createWorkorder={createWorkorder}
                    isAreaInFilter={filterController.isAreaInFilter}
                    reloadScheduleWorkorders={() => {
                        getWorkorderList({
                            params: { schedule: scheduleDetail.id },
                        })
                        getAreaStatusTags(scheduleDetail.id)
                    }}
                    workordersLoading={workordersLoading}
                    apartmentVendorList={apartmentVendorList}
                    onAfterSchedule={() => {
                        if (!scheduleDetail?.id) {
                            return
                        }
                        axiosInstance
                            .get<DoubleTransferChainResponse>(
                                `workorder/schedule/${scheduleDetail.id}/double-transfer-chains/`,
                            )
                            .then((response) => {
                                if (response.data?.chains?.length) {
                                    setPendingDtChainAlert(true)
                                }
                            })
                            .catch(() => undefined)
                    }}
                />
            )}

            <UnitInspectionDetailForm
                unitConfigMap={unitConfigMap}
                inventoryConfigMap={inventoryConfigMap}
                unitInspection={unitInspectionDetail ?? undefined}
                areaStatusMap={areaStatusTagMap}
                isInventoryInspectionInFilter={() => true}
                onClose={onCloseUnitInspectionDrawer}
                setUnitInspection={setUnitInspectionDetail}
                open={openUnitInspectionDrawer}
                loading={loadingUnitInspectionDetail}
            />
        </Container>
    )
}
